/**
 * T22 — Remote Console Confirm reconcile helpers.
 * Pure logic only: never silently retries update_kk_app_meta.
 */

import type { ConfirmedPlayerSettings } from "@/features/devices/confirmedPlayerSettings";

export const REMOTE_SETTINGS_CONFIRMATION_UNAVAILABLE =
  "Confirmation unavailable. Remote Console could not refresh device-confirmed settings. Draft values are not applied.";

export const WAKE_TIME_LABEL = "Wake Time — Music Resumes";
export const SLEEP_TIME_LABEL = "Sleep Time — Music Stops";

/** Requested settings captured at Confirm click (local draft). */
export type RequestedRemoteSettings = {
  volume: number;
  master: number;
  appLock: boolean;
  wakeSleep: boolean;
  startTime: string;
  endTime: string;
  /** T24 — explicit requested weekend sleep (always sent with update_kk_app_meta). */
  sleepSaturday: boolean;
  sleepSunday: boolean;
};

export type PostConfirmReconcileInput = {
  operatorState: "successful" | "unsuccessful";
  /** Fleet GET / refetch succeeded */
  fleetReachable: boolean;
  confirmedComplete: boolean;
};

export type PostConfirmReconcileAction =
  | { type: "apply-confirmed"; clearTouched: true }
  | { type: "confirmation-unavailable"; clearTouched: true }
  | { type: "keep-successful-path" };

/**
 * After Confirm poll terminates:
 * - successful → caller applies confirmed snapshot (existing path)
 * - unsuccessful + Fleet reachable + complete snap → reconcile to confirmed
 * - unsuccessful + Fleet unreachable / incomplete → Confirmation unavailable
 * Never returns a silent re-send of update_kk_app_meta.
 */
export function decidePostConfirmReconcile(
  input: PostConfirmReconcileInput
): PostConfirmReconcileAction {
  if (input.operatorState === "successful") {
    return { type: "keep-successful-path" };
  }
  if (input.fleetReachable && input.confirmedComplete) {
    return { type: "apply-confirmed", clearTouched: true };
  }
  return { type: "confirmation-unavailable", clearTouched: true };
}

/**
 * Matches Player SleepWakeScheduleExtensions: sleep after wake on the
 * clock (e.g. Sleep 17:00 / Wake 01:00 or Sleep 23:00 / Wake 06:00)
 * spans midnight. Sleep before wake (e.g. 01:00 / 06:00) is same-day.
 */
export function isCrossMidnightSchedule(
  wakeTime: string | null | undefined,
  sleepTime: string | null | undefined
): boolean {
  if (!wakeTime || !sleepTime) return false;
  if (!/^\d{2}:\d{2}$/.test(wakeTime) || !/^\d{2}:\d{2}$/.test(sleepTime)) {
    return false;
  }
  if (sleepTime === wakeTime) return false;
  return sleepTime > wakeTime;
}

export function formatWakeSleepPair(
  wakeSleep: boolean,
  startTime: string | null | undefined,
  endTime: string | null | undefined
): string {
  if (!wakeSleep) return "Off";
  const wake = startTime || "--:--";
  const sleep = endTime || "--:--";
  return `Wake ${wake} / Sleep ${sleep}`;
}

export const WEEKEND_NOT_REPORTED = "Not reported";

/**
 * T24 — confirmed weekend flag display. null/undefined is NOT confirmed false.
 */
export function formatWeekendFlag(
  value: boolean | null | undefined
): "On" | "Off" | typeof WEEKEND_NOT_REPORTED {
  if (value === true) return "On";
  if (value === false) return "Off";
  return WEEKEND_NOT_REPORTED;
}

export function formatWeekendSleepPair(
  sleepSaturday: boolean | null | undefined,
  sleepSunday: boolean | null | undefined
): string {
  return `Sat ${formatWeekendFlag(sleepSaturday)} / Sun ${formatWeekendFlag(
    sleepSunday
  )}`;
}

export function pendingRequestedVsConfirmedCopy(args: {
  requested: RequestedRemoteSettings | null;
  confirmed: ConfirmedPlayerSettings | null;
}): string | null {
  if (!args.requested) return null;
  const reqWeekend = formatWeekendSleepPair(
    args.requested.sleepSaturday,
    args.requested.sleepSunday
  );
  const req = `${formatWakeSleepPair(
    args.requested.wakeSleep,
    args.requested.startTime,
    args.requested.endTime
  )}; ${reqWeekend}`;
  const conf = args.confirmed
    ? `${formatWakeSleepPair(
        args.confirmed.wakeSleepEnabled === true,
        args.confirmed.startTime,
        args.confirmed.endTime
      )}; ${formatWeekendSleepPair(
        args.confirmed.sleepSaturday,
        args.confirmed.sleepSunday
      )}`
    : "unknown";
  return `Requested: ${req}. Device confirmed: ${conf}.`;
}

export function requestedFromConfirmValues(values: {
  volume: number;
  master: number;
  appLock: boolean;
  wakeSleep: boolean;
  startTime: string;
  endTime: string;
  sleepSaturday: boolean;
  sleepSunday: boolean;
}): RequestedRemoteSettings {
  return {
    volume: values.volume,
    master: values.master,
    appLock: values.appLock,
    wakeSleep: values.wakeSleep,
    startTime: values.startTime,
    endTime: values.endTime,
    sleepSaturday: values.sleepSaturday,
    sleepSunday: values.sleepSunday,
  };
}
